import { useEffect, useState } from "react";
import api from "@/lib/api";
import { ImageOff, Loader2 } from "lucide-react";

/**
 * Renders a recce photo. Accepts either:
 *   - External URL (Drive/Dropbox etc.) — rendered directly as <img>
 *   - Internal ref `emergent:<storage_path>` — fetched with auth,
 *     converted to a blob URL, then rendered as <img>
 * Blob URL is revoked on unmount to prevent leaks.
 */
export default function AuthedImage({
  photo,        // string — the photo_urls entry
  projectId,
  locationId,
  className = "",
  onClick,
  testid,
}) {
  const [src, setSrc]         = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(false);

  useEffect(() => {
    let alive = true;
    let objectUrl = null;

    async function run() {
      if (!photo) return;
      // External URL — no auth needed
      if (!photo.startsWith("emergent:")) {
        if (alive) { setSrc(photo); setLoading(false); }
        return;
      }
      try {
        const { data } = await api.get(
          `/projects/${projectId}/locations/${locationId}/photos/serve`,
          { params: { path: photo }, responseType: "blob" },
        );
        objectUrl = URL.createObjectURL(data);
        if (alive) { setSrc(objectUrl); setLoading(false); }
      } catch {
        if (alive) { setError(true); setLoading(false); }
      }
    }
    void run();
    return () => {
      alive = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [photo, projectId, locationId]);

  if (loading) {
    return (
      <div className={`bg-black/40 border border-white/10 flex items-center justify-center ${className}`}
        data-testid={testid ? `${testid}-loading` : undefined}>
        <Loader2 size={12} className="animate-spin text-white/40" />
      </div>
    );
  }
  if (error || !src) {
    return (
      <div className={`bg-black/40 border border-white/10 flex items-center justify-center ${className}`}
        data-testid={testid ? `${testid}-error` : undefined}>
        <ImageOff size={12} className="text-white/40" />
      </div>
    );
  }
  return (
    <img src={src} alt="recce" onClick={onClick}
      className={`object-cover ${className}`}
      data-testid={testid} />
  );
}
